<template>
  <div
    v-if="store.selectedDashboard"
    v-on-click-outside="handleOnClickOutside"
    class="filter-single container"
    :title="store.selectedDashboard.name"
  >
    <div
      v-focus
      tabindex="0"
      data-product-tour="step-2-1"
      class="select-box"
      @keydown.enter.prevent="toggleMenuOpen"
      @keydown.escape.prevent="isMenuOpen = false"
      @click="toggleMenuOpen"
    >
      <label
        :class="[
          'selected-label',
          'capitalize-letter',
          { 'selection-changed': filterChanged },
        ]"
      >
        {{ store.selectedDashboard.name }}
        {{ filterChanged ? "*" : "" }}
      </label>

      <div class="select-arrow">
        <i
          class="fa-solid"
          :class="isMenuOpen ? 'fa-chevron-up' : 'fa-chevron-down'"
        ></i>
      </div>
    </div>

    <div
      v-if="isMenuOpen"
      class="menu"
      data-product-tour="step-2-2"
      @keydown.escape.prevent="isMenuOpen = false"
    >
      <CreateDashboardButton />

      <SelectSearchBar
        size="md"
        class="select-search-bar"
        v-model.trim="searchField"
      />
      <ul ref="listOfItems" class="items-list">
        <li
          v-for="item in searchedItems"
          :key="item._id"
          v-focus
          tabindex="0"
          class="item"
          :class="{
            active: store.selectedDashboard._id === item._id,
            paywall: !canApplyCustomDashboard(item._id),
          }"
          @click="handleOnClick(item._id)"
          @keydown.enter.prevent="handleOnSelectDashboard(item._id)"
        >
          <span class="item-text">
            {{ item.name }}
          </span>
          <span v-if="item.is_custom && item._id !== DEFAULT_DASHBOARD_ID">
            <i class="fa-kit fa-sv-symbole"></i>
          </span>
          <span v-if="!canApplyCustomDashboard(item._id)">
            <PermissionLevelIcon
              class="permission-level-icon"
              :permission-level="PermissionLevel.BASIC"
            />
          </span>
        </li>
      </ul>

      <RouterLink :to="{ name: 'Veilles' }">
        <Button
          @click="isMenuOpen = false"
          class="btn-go-to-dashboards"
          type="link"
          color="nocolor"
        >
          <template #iconLeft>
            <i class="fa-duotone fa-solid fa-bookmark"></i>
          </template>
          Gérer mes veilles
        </Button>
      </RouterLink>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, inject } from "vue";
import { vOnClickOutside } from "@vueuse/components";

import SelectSearchBar from "@follaw-fr/components/reusable/atoms/SelectSearchBar.vue";
import { searchNormalized } from "@follaw-fr/utils/helper.utils";
import Button from "@follaw-fr/components/reusable/atoms/Button.vue";
import { useDashboardsStore } from "@follaw-fr/stores/dashboards";
import CreateDashboardButton from "@follaw-fr/components/general/molecules/CreateDashboardButton.vue";
import { useFilterChangeDetection } from "@follaw-fr/composables/useFilterChangeDetection";
import { useUserSettingsStore } from "@follaw-fr/stores/user-settings";
import { DEFAULT_DASHBOARD_ID } from "@follaw-fr/constants/dashboard.constant";
import { usePermissionGuard } from "@follaw-fr/composables/usePermissionGuard";
import { PermissionLevel } from "@follaw-fr/constants/enums/permission-level.enum";
import PermissionLevelIcon from "@follaw-fr/components/general/atoms/PermissionLevelIcon.vue";
import { openDashboardPaywallModalKey } from "@follaw-fr/constants/injection-keys";
import { storeToRefs } from "pinia";
import { useUserStore } from "@/follaw-fr/stores/user";

const isMenuOpen = ref(false);
const searchField = ref("");
const listOfItems = ref<HTMLBaseElement | null>(null);
const store = useDashboardsStore();
const { hasPermission } = useUserSettingsStore();
const { productTourCompleted } = storeToRefs(useUserStore());
const { permissionGuard } = usePermissionGuard();

const openDashboardPaywallModal = inject(openDashboardPaywallModalKey);

const canApplyCustomDashboard = computed(
  () => (dashboardId: number) =>
    dashboardId === DEFAULT_DASHBOARD_ID || hasPermission(PermissionLevel.BASIC)
);

const { filterChanged } = useFilterChangeDetection();

const searchedItems = computed(() =>
  store.dashboards.filter((item) =>
    searchNormalized(item.name, searchField.value)
  )
);

const handleOnClick = (dashboardId: number) => {
  if (dashboardId === DEFAULT_DASHBOARD_ID) {
    handleOnSelectDashboard(dashboardId);
  } else {
    handleOnSelectCustomDashboard(dashboardId);
  }
};

const handleOnClickOutside = () => {
  if (productTourCompleted.value) {
    isMenuOpen.value = false;
  }
};

const handleOnSelectDashboard = (dashboardId: number) => {
  store.applyDashboard(dashboardId);
  isMenuOpen.value = false;
};

const handleOnSelectCustomDashboard = (dashboardId: number) => {
  permissionGuard({
    permissionLevel: PermissionLevel.BASIC,
    action: () => handleOnSelectDashboard(dashboardId),
    fallBack: openDashboardPaywallModal,
  });
};

async function toggleMenuOpen() {
  isMenuOpen.value = !isMenuOpen.value;

  if (isMenuOpen.value) {
    await nextTick();
    const activeItem =
      listOfItems?.value?.getElementsByClassName("item active")?.[0];
    activeItem?.scrollIntoView();
  }
}
</script>

<style lang="scss" scoped>
/* Purple & Topbar styles
========================================================================== */
.container {
  color: var(--clr-n8);
  .select-box {
    box-shadow: 0 3px 5px #00000014;
    border-radius: 15px;
    color: white;
    background: var(--clr-p8);
  }
  .menu {
    box-shadow: 0 1px 6px #0000005d;
    border-radius: 13px;
    display: flex;
    flex-direction: column;
    min-width: 350px;
    color: var(--clr-n8);
  }

  .active {
    background-color: var(--clr-p2);
  }
  .paywall {
    cursor: not-allowed;
    background-color: var(--clr-n2);
    color: var(--clr-n7);
  }

  ::-webkit-scrollbar-thumb {
    background-color: var(--clr-p2);
  }
}

/* Whole component
========================================================================== */
.filter-single {
  position: relative;
  -webkit-user-select: none;
  user-select: none;
}

ul {
  padding: 0;
  margin: 0;
}

/* Input
========================================================================== */
.select-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 220px;
  height: 30px;
  padding: 4px 15px;
  background: white;
  cursor: pointer;
  .selected-label {
    flex: 1;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    &.selection-changed {
      font-style: italic;
    }
  }
  .select-arrow {
    margin-left: 4px;
    pointer-events: none;
  }
}

/* Popover Menu
========================================================================== */
.menu {
  position: absolute;
  z-index: 3;
  margin-top: 8px;
  left: 0;
  max-height: 435px;
  padding: 8px;
  background: white;
  gap: 8px;
  .items-list {
    max-height: 395px;
    overflow-y: scroll;
  }
}

/* Individual items
========================================================================== */
.item {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 4px 8px;
  margin: 4px 2px;
  line-height: 1.5;
  border-radius: 4px;
  cursor: pointer;

  &:first-letter {
    text-transform: uppercase;
  }

  &:hover {
    background-color: var(--clr-p1);
  }

  &.active:hover {
    background-color: var(--clr-p2);
  }
}

/* Scrollbar
========================================================================== */
::-webkit-scrollbar {
  width: 6px;
}
::-webkit-scrollbar-thumb {
  border-radius: 7px;
}
</style>

<style>
.save-button {
  width: 100%;
}
.btn-go-to-dashboards {
  width: 100%;
  box-shadow: none !important;
  i {
    color: var(--clr-p5);
  }
}
</style>
